import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  ProgressBar,
  Stack,
  useSnackbars,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import plur from 'plur';
import { useGenerateDataIndexDescriptions } from '@/services/db/data-index/mutations';
import type { DataIndexAnalyticsType } from '../utils/types';

/**
 * Ask the model to describe several rows at once.
 *
 * Unlike the single-row button, what comes back is saved: there is no one
 * box to put fifty descriptions in, and reviewing them one at a time is what
 * the row editors are for afterwards.
 */

export type GenerateDescriptionsModalData = {
  type: DataIndexAnalyticsType;
  /** What the rows are called: "attribute" or "event". */
  noun: string;
  names: readonly string[];
  objectTypeId?: number;
  workspaceId: number;
  /** Saves the descriptions the model wrote; resolves to whether that worked. */
  onGenerated: (descriptions: Record<string, string>) => Promise<boolean>;
};

/**
 * How full the bar gets before the answer arrives.
 *
 * The request reports no progress of its own, so the bar is an estimate. It
 * stops short of full on purpose: finishing before the work does would say
 * something the page does not know.
 */
const MAX_PROGRESS = 90;
const ESTIMATED_DURATION_MS = 2000;
const TICK_MS = 25;

export function GenerateDescriptionsModal({
  data,
  modalState,
  onClose,
  onConfirm,
}: PlumaManagedModalProps<GenerateDescriptionsModalData>) {
  const { type, noun, names, objectTypeId, workspaceId, onGenerated } = data;
  const snackbars = useSnackbars();
  const [progress, setProgress] = useState<number | null>(null);
  const tick = useRef<ReturnType<typeof setInterval> | null>(null);
  // The request is not cancelled when the modal closes, so a late answer
  // would still be saved over every selected row. Cancel has to mean the
  // answer is discarded, not merely that the modal is gone.
  const isCancelled = useRef(false);
  const isRunning = useRef(false);
  const generate = useGenerateDataIndexDescriptions({ workspaceId });

  // The bar is driven by a timer, and a route change can unmount the modal
  // while the request is still out. Going away counts as cancelling, whether
  // that was Cancel, the close button, or a navigation.
  useEffect(() => {
    // Cleared on the way in as well as set on the way out: React runs an
    // effect's cleanup before its real setup in development, so a modal that
    // only ever set this on unmount mounted already cancelled, and every
    // answer the model gave was discarded.
    isCancelled.current = false;

    return () => {
      isCancelled.current = true;
      clearInterval(tick.current ?? undefined);
    };
  }, []);

  const isGenerating = progress != null;
  const subject = `${names.length} selected ${plur(noun, names.length)}`;

  const run = async () => {
    // A ref, not the rendered state: two clicks in one tick both read the
    // state as false, and the second would start a timer nothing clears and
    // ask the model a second time. The page this replaces drops the second
    // click through `task({ drop: true })`.
    if (isRunning.current) {
      return;
    }
    isRunning.current = true;
    const startedAt = Date.now();
    tick.current = setInterval(() => {
      const elapsed = (Date.now() - startedAt) / ESTIMATED_DURATION_MS;
      const next = Math.min(Math.round(elapsed * 100), MAX_PROGRESS);
      setProgress(next);
      // Nothing left to say until the answer arrives.
      if (next === MAX_PROGRESS) {
        clearInterval(tick.current ?? undefined);
      }
    }, TICK_MS);
    setProgress(0);

    try {
      const written = await generate.mutateAsync({ type, items: [...names], objectTypeId });
      if (isCancelled.current) {
        return;
      }
      const saved = await onGenerated(Object.fromEntries(written.map((item) => [item.name, item.description])));
      // Checked again: saving the descriptions is its own request, and the
      // modal can be dismissed while it is out. Confirming then would clear
      // the selection behind somebody who had already left.
      if (isCancelled.current) {
        return;
      }
      if (saved) {
        onConfirm();
        return;
      }
    } catch {
      if (isCancelled.current) {
        return;
      }
      snackbars.create({
        id: 'data-index-generate-descriptions',
        variant: 'critical',
        message: `Failed to generate descriptions for ${plur(noun, names.length)}`,
      });
    } finally {
      clearInterval(tick.current ?? undefined);
      setProgress(null);
      isRunning.current = false;
    }
  };

  /**
   * Leaving, by any route: Cancel, the close button, Escape, or the overlay.
   * They all reach `onClose`, and each one means the answer still in flight is
   * no longer wanted — the request is not cancellable, so what arrives has to
   * be discarded instead.
   */
  const close = () => {
    isCancelled.current = true;
    onClose();
  };

  return (
    <Modal state={modalState} size="sm" title={`Generate descriptions for ${plur(noun, names.length)}`} onClose={close}>
      <ModalBody>
        <Stack gap="200">
          <Paragraph>
            {isGenerating
              ? `Generating descriptions for ${subject}...`
              : `This will generate AI-powered descriptions for the ${subject}.`}
          </Paragraph>
          {isGenerating ? (
            <ProgressBar ariaLabel={`Generating descriptions for ${subject}`} value={progress} size="sm" />
          ) : null}
        </Stack>
      </ModalBody>
      <ModalFooter>
        {/* Left usable mid-request: the answer can be slow, and waiting for
            it is not something anyone should be forced into. */}
        <Button variant="secondary" onClick={close}>
          Cancel
        </Button>
        <Button variant="primary" isLoading={isGenerating} onClick={() => void run()}>
          Generate descriptions
        </Button>
      </ModalFooter>
    </Modal>
  );
}
